import React, { useState, useEffect } from "react";
import GenericModal from "../../../Ui/Modal";
import CustomPagination from "../Pagination";
import SearchBar from "../SearchBarActivities";
import ButtonComponent from "../../../Ui/Button";
import { Tenants, Property, Unit } from "../../../../service";
import useApiHandler from "../../../hooks/ApiHandler";
// Input field component
const TenantInputField = ({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  options = [],
  disabled = false,
  className = "",
}) => {
  if (type === "select") {
    return (
      <div className={`flex-1 min-w-[180px] ${className}`}>
        <label className="block text-[12px] leading-[18px] font-semibold text-[#333333] mb-1">
          {label}
        </label>
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="w-full bg-[#F8F8F8] font-neue-haas rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div className={`flex-1 min-w-[180px] mb-0 ${className}`}>
      <label className="block text-[12px] leading-[18px] font-semibold text-[#333333] mb-1">
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full bg-[#F8F8F8] font-neue-haas rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
      />
    </div>
  );
};
const EditTenant = ({
  setOpen,

  setModalState,
  propertyId,
  setPropertyId,
}) => {
  const { callApi, loading } = useApiHandler();
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [originalTenants, setOriginalTenants] = useState([]); // Track original data
  const [filteredTenants, setFilteredTenants] = useState(tenants);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(4);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [saveStatus, setSaveStatus] = useState({});
  const [inviteStatus, setInviteStatus] = useState({});
  const [unitOptions, setUnitOptions] = useState([]);

  // Generate unit options from property data
  const generateUnitOptions = (propertyData) => {
    console.log(propertyData, "propertyData");
    if (propertyData.length > 0) {
      const options = propertyData.map((unit) => {
        // Create a meaningful label for the unit
        let label = "";
        if (unit.unit_apt) {
          label = unit.unit_apt;
        } else if (propertyData.property_type === "Commercial Building") {
          label = `Commercial Unit ${unit.id}`;
        } else if (propertyData.property_type === "Apartment") {
          label = `Apartment ${unit.id}`;
        } else {
          label = `Unit ${unit.id}`;
        }

        // Add status information if available
        if (unit.status) {
          label += ` (${unit.status})`;
        }

        return {
          label: label,
          value: unit.id.toString(),
          unitData: unit,
        };
      });

      setUnitOptions(options);
    } else {
      setUnitOptions([]);
    }
  };

  // Search
  const fetchPropertyDetails = () => {
    if (propertyId) {
      callApi(() => Property.getPropertyById(propertyId), {
        onSuccess: (res) => {
          console.log(res, "res.data?.units");
          generateUnitOptions(res.data?.units);
        },
        onError: (error) => {
          console.error("Error fetching property details:", error);
        },
        errorMessage: "Failed to fetch property details",
      });
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    const filtered = tenants.filter((tenant) =>
      tenant.tenantName.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredTenants(filtered);
    setCurrentPage(1);
  };

  // Check if tenant has changes
  const hasChanges = (tenantId) => {
    const currentTenant = tenants.find((t) => t.id === tenantId);
    const originalTenant = originalTenants.find((t) => t.id === tenantId);

    if (!currentTenant || !originalTenant) return false;

    return (
      currentTenant.tenantName !== originalTenant.tenantName ||
      currentTenant.tenantEmail !== originalTenant.tenantEmail ||
      currentTenant.unit !== originalTenant.unit ||
      currentTenant.phoneNumber !== originalTenant.phoneNumber ||
      currentTenant.status !== originalTenant.status
    );
  };

  // Update tenant input
  const handleTenantUpdate = (tenantId, field, value) => {
    setTenants((prev) =>
      prev.map((tenant) =>
        tenant.id === tenantId
          ? { ...tenant, [field]: value, status: "PENDING" }
          : tenant
      )
    );

    // Update status to dirty using tenantId
    setSaveStatus((prevStatus) => ({
      ...prevStatus,
      [tenantId]: "dirty",
    }));
  };
  // Save
  const handleSaveTenant = async (rowIndex) => {
    const tenant = tenants[rowIndex];
    if (!tenant) return;
    const tenantId = tenant.id;

    setSaveStatus((prevStatus) => ({
      ...prevStatus,
      [tenantId]: "saving",
    }));

    try {
      // Prepare the data for the API call
      const values = {
        tenant_name: tenant.tenantName,
        tenant_email: tenant.tenantEmail,
        unit: tenant.unit,
        phone_number: tenant.phoneNumber,
        property: propertyId,
      };

      // Use tenant_id if available, otherwise fall back to id
      const tenantIdentifier = tenant.tenant_id || tenant.id;

      await callApi(
        () => Tenants.editTenantInvitation(tenantIdentifier, values),
        {
          onSuccess: (res) => {
            setSaveStatus((prevStatus) => ({
              ...prevStatus,
              [tenantId]: "saved",
            }));

            // Reset invite status after successful save
            setInviteStatus((prevStatus) => ({
              ...prevStatus,
              [tenantId]: "idle",
            }));

            // Refresh the tenant data from server
            fetchTenants();
          },
          onError: (error) => {
            console.error("Error updating tenant:", error);
            setSaveStatus((prevStatus) => ({
              ...prevStatus,
              [tenantId]: "dirty", // Reset to dirty state on error
            }));
          },
          errorMessage: "Failed to update tenant information",
        }
      );
    } catch (error) {
      console.error("Error updating tenant:", error);
      setSaveStatus((prevStatus) => ({
        ...prevStatus,
        [tenantId]: "dirty", // Reset to dirty state on error
      }));
    }
  };
  // Send Invite
  const handleSendInvite = async (rowIndex) => {
    const tenant = tenants[rowIndex];
    if (!tenant) return;
    const tenantId = tenant.id;

    setInviteStatus((prevStatus) => ({
      ...prevStatus,
      [tenantId]: "sending",
    }));

    try {
      // Use tenantIdentifier to check if invite already exists
      const tenantIdentifier = tenant.tenant_id || tenant.id;

      // Prepare the payload for the API call
      const payload = {
        tenant_name: tenant.tenantName,
        tenant_email: tenant.tenantEmail,
        property: propertyId,
        unit: tenant.unit,
      };

      const apiCall = tenantIdentifier
        ? () => Tenants.resendTenantInvitation(tenantIdentifier, payload)
        : () => Tenants.singleTenantInvitation(payload);

      await callApi(apiCall, {
        onSuccess: (res) => {
          setInviteStatus((prevStatus) => ({
            ...prevStatus,
            [tenantId]: "sent",
          }));

          // Refresh the tenant data from server
          fetchTenants();

          // Reset to idle after showing "Sent" for a moment
          setTimeout(() => {
            setInviteStatus((prevStatus) => ({
              ...prevStatus,
              [tenantId]: "idle",
            }));
          }, 1500);
        },
        onError: (error) => {
          console.error("Error sending invite:", error);
          setInviteStatus((prevStatus) => ({
            ...prevStatus,
            [tenantId]: "idle",
          }));
        },
        errorMessage: "Failed to send invite",
      });
    } catch (error) {
      console.error("Error sending invite:", error);
      setInviteStatus((prevStatus) => ({
        ...prevStatus,
        [tenantId]: "idle",
      }));
    }
  };
  // Remove
  const handleRemoveTenant = async (tenantId) => {
    try {
      // Find the current tenant and original tenant
      const currentTenant = tenants.find((t) => t.id === tenantId);
      const originalTenant = originalTenants.find((t) => t.id === tenantId);

      // Use original unit ID if user changed unit but didn't save it
      const unitIdToUse = originalTenant?.unit || currentTenant?.unit;
      const tenantStatus = currentTenant?.status || originalTenant?.status;

      // Check if tenant status is ACCEPTED
      if (tenantStatus === "ACCEPTED" && unitIdToUse) {
        // If status is ACCEPTED, use removeTenantFromUnit API
        await callApi(() => Unit.removeTenantFromUnit(unitIdToUse), {
          onSuccess: (res) => {
            // Refresh the tenant data from server
            fetchTenants();
          },
          onError: (error) => {
            console.error("Error removing tenant from unit:", error);
          },
          errorMessage: "Failed to remove tenant from unit",
        });
      } else {
        // For all other statuses, use removeTenantInvitation API
        await callApi(() => Tenants.removeTenantInvitation(tenantId), {
          onSuccess: (res) => {
            // Refresh the tenant data from server
            fetchTenants();
          },
          onError: (error) => {
            console.error("Error removing tenant:", error);
          },
          errorMessage: "Failed to remove tenant",
        });
      }
    } catch (error) {
      console.error("Error removing tenant:", error);
    }
  };

  useEffect(() => {
    const filtered = tenants.filter((tenant) =>
      tenant.tenantName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTenants(filtered);
  }, [tenants]);
  const fetchTenants = async () => {
    if (propertyId) {
      await callApi(
        () =>
          Tenants.tenantInvitationList(propertyId, currentPage, resultsPerPage),
        {
          onSuccess: (res) => {
            // Ensure all tenants have a status field, defaulting to "PENDING" if not present
            const tenantsWithStatus = res.data?.results.map((tenant) => ({
              ...tenant,
              status: tenant.status || "PENDING",
            }));
            setTenants(tenantsWithStatus);
            setOriginalTenants(tenantsWithStatus); // Store original data for comparison
            setTotalCount(res.data?.count || res.data?.length || 0);
          },
        }
      );
    }
  };

  // Handle results per page change
  const handleResultsPerPageChange = (newResultsPerPage) => {
    setResultsPerPage(newResultsPerPage);
    setCurrentPage(1); // Reset to first page when changing results per page
  };

  useEffect(() => {
    fetchTenants();
    fetchPropertyDetails();
  }, [currentPage, resultsPerPage]);

  // useEffect(() => {
  //   if (propertyId) {
  //     fetchTenants();
  //   }
  // }, [propertyId]);
  // For server-side pagination, we use the tenants directly (already paginated from API)
  // For client-side search, we still filter the current page data
  const currentTenants = filteredTenants;
  const handleClose = () => {
    setPropertyId(null);
    setModalState("");
  };
  return (
    <div>
      <GenericModal
        maxWidth="966px"
        open={true}
        onClose={handleClose}
        title="Edit Tenants"
        footer={
          <div className="flex w-full sm2:gap-4 gap-2 justify-between sm2:flex-row flex-col ">
            <ButtonComponent
              fontSize="14px"
              bgColor="#52C1711A"
              color="#52C171"
              borderColor="#52C171"
              paddingY="10px"
              onClick={() => setModalState("addTenant")}
            >
              Add Tenant
            </ButtonComponent>
            <div className="flex justify-between sm2:gap-4 gap-2 sm2:flex-row flex-col">
              <ButtonComponent
                fontSize="14px"
                onClick={() => setModalState("addProperty")}
                paddingY="10px"
              >
                Edit Property Details
              </ButtonComponent>

              <ButtonComponent
                fontSize="14px"
                paddingY="10px"
                bgColor="#3CAB5C"
                color="#fff"
                onClick={handleClose}
              >
                Exit
              </ButtonComponent>
            </div>
          </div>
        }
      >
        <div className="px-4">
          {/* Property Info Section */}

          <SearchBar
            placeholder="Search by name"
            onSearch={handleSearch}
            label="Search Tenants"
            select={[{ label: "Sort by", values: ["Name", "Unit", "Status"] }]}
          />
          {tenants.length > 0 ? (
            <div>
              {currentTenants.map((tenant, idx) => {
                const rowIndex = tenants.findIndex((t) => t.id === tenant.id);
                return (
                  <div
                    key={tenant.id}
                    className="md:py-4 py-2 flex md:gap-4 gap-2 lg:flex-row flex-col justify-center lg:items-center"
                  >
                    <div className="flex flex-wrap gap-4 w-full">
                      <TenantInputField
                        label="Tenant Name"
                        type="text"
                        placeholder="Auto"
                        value={tenant.tenantName}
                        onChange={(e) =>
                          handleTenantUpdate(
                            tenant.id,
                            "tenantName",
                            e.target.value
                          )
                        }
                      />
                      <TenantInputField
                        label="Unit"
                        type="select"
                        options={unitOptions}
                        value={tenant.unit || ""}
                        onChange={(e) =>
                          handleTenantUpdate(tenant.id, "unit", e.target.value)
                        }
                        disabled={loading}
                      />
                      <TenantInputField
                        label="Email"
                        type="email"
                        placeholder="Auto"
                        value={tenant.tenantEmail}
                        onChange={(e) =>
                          handleTenantUpdate(
                            tenant.id,
                            "tenantEmail",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <div className="flex md:gap-4 gap-2 md:max-h-[30px]  sm2:flex-row flex-col">
                      {/* Save */}
                      <ButtonComponent
                        paddingY="8px"
                        paddingX="16px"
                        onClick={() => handleSaveTenant(rowIndex)}
                        disabled={
                          saveStatus[tenant.id] === "saving" ||
                          !hasChanges(tenant.id)
                        }
                        bgColor={
                          saveStatus[tenant.id] === "saving"
                            ? "#9E9E9E" // darker gray
                            : saveStatus[tenant.id] === "saved"
                            ? "#52C2711A" // green background
                            : hasChanges(tenant.id)
                            ? "#D4B1001A" // yellow background
                            : "#F8F8F8" // default gray
                        }
                        color={
                          saveStatus[tenant.id] === "saving"
                            ? "#FFFFFF" // white on darker gray
                            : saveStatus[tenant.id] === "saved"
                            ? "#52C271" // green text
                            : hasChanges(tenant.id)
                            ? "#D4B100" // yellow text
                            : "#242424" // default text
                        }
                        borderColor={
                          saveStatus[tenant.id] === "saving"
                            ? "#9E9E9E"
                            : saveStatus[tenant.id] === "saved"
                            ? "#52C271" // green border
                            : hasChanges(tenant.id)
                            ? "#D4B100" // yellow border
                            : "#ECECEC" // default border
                        }
                      >
                        {saveStatus[tenant.id] === "saving"
                          ? "Saving..."
                          : saveStatus[tenant.id] === "saved"
                          ? "Saved"
                          : "Save"}
                      </ButtonComponent>
                      {/* Invite */}
                      <ButtonComponent
                        paddingY="8px"
                        paddingX="16px"
                        onClick={() => handleSendInvite(rowIndex)}
                        disabled={
                          saveStatus[tenant.id] === "dirty" ||
                          inviteStatus[tenant.id] === "sending"
                        }
                        bgColor={
                          inviteStatus[tenant.id] === "sending"
                            ? "#BDBDBD"
                            : inviteStatus[tenant.id] === "sent"
                            ? "#52C2711A"
                            : "#FFFFFF"
                        }
                        color={
                          inviteStatus[tenant.id] === "sending"
                            ? "#FFFFFF"
                            : inviteStatus[tenant.id] === "sent"
                            ? "#52C271"
                            : "#242424"
                        }
                        borderColor={
                          inviteStatus[tenant.id] === "sending"
                            ? "#9E9E9E"
                            : inviteStatus[tenant.id] === "sent"
                            ? "#52C271"
                            : "#ECECEC"
                        }
                      >
                        {inviteStatus[tenant.id] === "sending"
                          ? "Sending..."
                          : inviteStatus[tenant.id] === "sent"
                          ? "Sent"
                          : hasChanges(tenant.id)
                          ? "Send Invite"
                          : (tenant.status === "PENDING" || tenant.is_sent || tenant.lastSentAt || tenant.resendCount > 0)
                          ? "Resend Invite"
                          : "Send Invite"}
                      </ButtonComponent>
                      {/* Remove */}
                      <ButtonComponent
                        paddingX="16px"
                        bgColor="#D400001A"
                        color="#D40000"
                        borderColor="#D40000"
                        paddingY="8px"
                        onClick={() => handleRemoveTenant(tenant.id)}
                      >
                        Remove
                      </ButtonComponent>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-[#595959]">
              <p className="text-[16px] font-[500]">
                No tenants found for this property
              </p>
              <p className="text-[14px] mt-2">
                Tenants will appear here once they have joined.
              </p>
            </div>
          )}
          <div>
            <CustomPagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              resultsPerPage={resultsPerPage}
              onResultsPerPageChange={handleResultsPerPageChange}
              totalCount={totalCount}
              padding={false}
            />
          </div>
        </div>
      </GenericModal>
    </div>
  );
};
export default EditTenant;
